import { Elements } from '@stripe/react-stripe-js'
import { Badge } from '@viastud/ui/badge'
import { Button } from '@viastud/ui/button'
import { Card } from '@viastud/ui/card'
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@viastud/ui/dialog'
import { SegmentedControl } from '@viastud/ui/segmented-control'
import type { policyMessages } from '@viastud/utils'
import { buildPolicyMessage } from '@viastud/utils'
import { Crown, LoaderCircle, Rocket, Star, X } from 'lucide-react'
import { useState } from 'react'

import { getStripe } from '@/lib/stripe'
import { useAuthStore } from '@/store/auth.store.ts'

import PaymentForm from '../payment-form'

const baseUrl: string = import.meta.env.VITE_WEB_BASE_URL

interface StudentSubscriptionOfferProps {
  clientSecret: string | null
  subscriptionOpen: boolean
  onSubscriptionOpenChange: (open: boolean) => void
  onHandleSubscribe: (commitment: boolean) => void
  isSubscribed: boolean
  isPending: boolean
  invoicePrice: number
  monthlyPrice: number | null
  promoCode?: string
}

export function StudentSubscriptionOffer({
  clientSecret,
  subscriptionOpen,
  onSubscriptionOpenChange,
  onHandleSubscribe,
  isSubscribed,
  isPending,
  promoCode,
  invoicePrice,
  monthlyPrice,
}: StudentSubscriptionOfferProps) {
  const { user } = useAuthStore()

  const OFFERS: { label: string; value: boolean }[] = [
    // { label: 'Avec engagement', value: true },
    { label: 'Sans engagement', value: false },
  ] as const
  const [commitment, setCommitment] = useState(false)

  const getPrice = () => {
    if (monthlyPrice === null) return '...'

    const priceWithCommitment = monthlyPrice
    const priceNoCommitment = monthlyPrice
    let basePrice = commitment ? priceWithCommitment : priceNoCommitment

    if (promoCode === 'VIASTUD_30_1_MOIS' && commitment) {
      basePrice = priceWithCommitment * (1 - 0.33)
    } else if (promoCode === 'VIASTUD_20_1_MOIS' && !commitment) {
      basePrice = priceNoCommitment - 30
    } else if (promoCode === 'VIASTUD_30' && commitment) {
      basePrice = priceWithCommitment * (1 - 0.33)
    }
    return `${basePrice.toFixed(2).replace('.', ',')}€`
  }

  const getPolicyMessageKey = (): keyof typeof policyMessages => {
    if (!promoCode) {
      return commitment ? 'BASE_COMMITMENT' : 'BASE_NO_COMMITMENT'
    }

    if (promoCode === 'VIASTUD_30_1_MOIS' && commitment) {
      return 'VIASTUD_30_1_MOIS'
    } else if (promoCode === 'VIASTUD_20_1_MOIS' && !commitment) {
      return 'VIASTUD_20_1_MOIS'
    } else if (promoCode === 'VIASTUD_30' && commitment) {
      return 'VIASTUD_30'
    } else {
      return commitment ? 'BASE_COMMITMENT' : 'BASE_NO_COMMITMENT'
    }
  }

  const getPolicyMessage = () => {
    try {
      const price = (invoicePrice / 100).toFixed(2).replace('.', ',')
      const monthly = String(monthlyPrice ?? '').replace('.', ',')
      return buildPolicyMessage(getPolicyMessageKey(), {
        first_price: price,
        monthly_price: monthly,
      })
    } catch {
      return ''
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Mise en avant de l'abonnement */}
      <Card className="border-none bg-blue-50/50 p-8 md:p-12">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex w-full flex-col items-center justify-between rounded-2xl bg-white p-6 md:flex-row md:gap-y-2">
            <div className="flex w-80 flex-col items-start justify-start">
              <div>
                {commitment && (
                  <Badge
                    variant="secondary"
                    className="mb-1 bg-blue-100 text-blue-700 hover:bg-blue-100"
                  >
                    Tarif préférentiel
                  </Badge>
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900">Abonnement illimité</h3>
              <SegmentedControl
                items={OFFERS}
                value={commitment}
                onChange={setCommitment}
                className="mt-2 self-start"
              />
            </div>
            <div className="flex w-60 flex-col items-center gap-y-4">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-gray-900">{getPrice()}</span>
                <span className="text-lg text-gray-500">/ mois</span>
              </div>

              {!isSubscribed && (
                <Button
                  disabled={!!user?.parentId || isPending}
                  className="w-full"
                  onClick={() => {
                    onHandleSubscribe(commitment)
                  }}
                >
                  {isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "S'abonner"}
                </Button>
              )}
            </div>
          </div>

          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-400">
              {commitment ? (
                <Star className="h-6 w-6 text-white" />
              ) : (
                <Crown className="h-6 w-6 text-white" />
              )}
            </div>
          </div>

          <h2 className="mb-2 text-3xl font-bold text-gray-900">Donne le meilleur de toi-même</h2>
          <p className="mb-10 max-w-2xl text-lg text-gray-600">
            Accès illimité à tous nos cours et ressources pour soutenir ta réussite scolaire
          </p>

          <div className="mb-12 grid w-full grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Rocket className="h-5 w-5" />
              </div>
              <span className="text-center font-medium text-gray-700">
                Cours particuliers illimités pour toi
              </span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Crown className="h-5 w-5" />
              </div>
              <span className="text-center font-medium text-gray-700">
                Suivi de progression détaillé
              </span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Star className="h-5 w-5" />
              </div>
              <span className="text-center font-medium text-gray-700">
                Ressources premium pour ta réussite
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Dialog open={subscriptionOpen} onOpenChange={onSubscriptionOpenChange}>
        <DialogContent className="flex w-full max-w-6xl flex-col gap-6">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-gray-950">
              Informations de paiement
            </DialogTitle>
            <DialogClose asChild>
              <Button variant="none" className="m-0 h-6 w-6 justify-start p-0">
                <X className="h-6 w-6" />
              </Button>
            </DialogClose>
          </DialogHeader>

          {clientSecret ? (
            <Elements
              options={{
                clientSecret,
                appearance: {
                  theme: 'flat',
                  variables: {
                    colorPrimary: '#3347FF',
                    colorBackground: '#EBF4FF',
                    colorText: '#334155',
                    colorDanger: '#df1b41',
                    borderRadius: '16px',
                  },
                },
                loader: 'auto',
              }}
              stripe={getStripe()}
            >
              <div className="space-y-4">
                <PaymentForm
                  returnUrl={`${baseUrl}/settings`}
                  policyMessage={invoicePrice ? getPolicyMessage() : ''}
                  gtmEventType={commitment ? 'abonnement_engagement' : 'abonnement_sans_engagement'}
                />
              </div>
            </Elements>
          ) : (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="h-8 w-8 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
